import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraTabBar } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ArrowLeft } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageBody, PageHero } from '../../../components/layout';
import { useLayoutStore } from '../../../lib/stores/LayoutStore';
import { CapabilitiesTab } from './CapabilitiesTab';
import { CompaniesTab } from './CompaniesTab';
import { ContactsTab } from './ContactsTab';
import { ContractsTab } from './Contracts/ContractsTab';
import { ContractVehiclesTab } from './ContractVehiclesTab';
import { GovernmentOrgsTab } from './GovernmentOrgsTab';
import { LaborCategoriesTab } from './LaborCategoriesTab';
import { PricingSheetsTab } from './PricingSheetsTab';
import { ProgramRoleTypesTab } from './ProgramRoleTypesTab';
import { RateBookTab } from './RateBookTab';
import { isRegistryTabKey, registryTabLabel } from './registryTabs';
import { safeReturnTo } from './returnTo';
const isTabKey = isRegistryTabKey;
const TAB_PANELS = {
    contracts: ContractsTab,
    companies: CompaniesTab,
    contacts: ContactsTab,
    govorgs: GovernmentOrgsTab,
    capabilities: CapabilitiesTab,
    vehicles: ContractVehiclesTab,
    lcats: LaborCategoriesTab,
    'rate-book': RateBookTab,
    roles: ProgramRoleTypesTab,
    'pricing-sheets': PricingSheetsTab
};
export function RegistriesPage() {
    // Routed at root-level /registries, outside DeliveryLayout — so this page
    // suppresses the legacy metadata band itself and owns its header inline.
    const setShowHeader = useLayoutStore((state)=>state.setShowHeader);
    useEffect(()=>{
        setShowHeader(false);
        return ()=>setShowHeader(true);
    }, [
        setShowHeader
    ]);
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const returnTo = safeReturnTo(params.get('returnTo'));
    // OD-48 RESOLVED 2026-10-29: Companies is the default tab (the mockup's
    // Labor Categories default is not adopted).
    const active = isTabKey(params.get('tab')) ? params.get('tab') : 'companies';
    const setActive = (next)=>{
        params.set('tab', next);
        // Every record, filter and sub-view belongs to the tab it was set on.
        for (const k of [
            'record',
            'q',
            'company',
            'vehicle',
            'lcat',
            'type',
            'unit',
            'burden',
            'period',
            'selected',
            'vehicleType',
            'agency',
            'history',
            'vehicleSection',
            'mapping',
            'anchor',
            'view',
            'entry',
            'inactive',
            'contactId'
        ])params.delete(k);
        setParams(params, {
            replace: true
        });
    };
    // Only the active panel mounts — same lifecycle Radix TabsContent had, so
    // per-tab queries still fire on first visit rather than all at once.
    const ActivePanel = TAB_PANELS[active];
    return /*#__PURE__*/ _jsx(PageBody, {
        children: /*#__PURE__*/ _jsxs("div", {
            "data-testid": Testids.deliveryRegistries.page,
            children: [
                returnTo && /*#__PURE__*/ _jsxs(SpecraButton, {
                    variant: "secondary",
                    className: "mb-4",
                    onClick: ()=>navigate(returnTo),
                    "data-testid": Testids.deliveryRegistries.backButton,
                    children: [
                        /*#__PURE__*/ _jsx(ArrowLeft, {
                            className: "mr-2 h-4 w-4"
                        }),
                        "Back"
                    ]
                }),
                /*#__PURE__*/ _jsx(PageHero, {
                    title: "Registries",
                    subtitle: "Companies, contacts, government orgs, contracts, vehicles, labor categories, and pricing sheets"
                }),
                /*#__PURE__*/ _jsx(SpecraTabBar, {
                    label: "Registries sections",
                    value: active,
                    onValueChange: setActive,
                    options: [
                        {
                            value: 'contracts',
                            label: registryTabLabel('contracts'),
                            testId: Testids.registryContracts.tab
                        },
                        {
                            value: 'companies',
                            label: registryTabLabel('companies'),
                            testId: Testids.deliveryRegistries.tabCompanies
                        },
                        {
                            value: 'contacts',
                            label: registryTabLabel('contacts'),
                            testId: Testids.deliveryRegistries.tabContacts
                        },
                        {
                            value: 'govorgs',
                            label: registryTabLabel('govorgs'),
                            testId: Testids.deliveryRegistries.tabGovernmentOrgs
                        },
                        {
                            value: 'capabilities',
                            label: registryTabLabel('capabilities'),
                            testId: Testids.deliveryRegistries.tabCapabilities
                        },
                        {
                            value: 'vehicles',
                            label: registryTabLabel('vehicles'),
                            testId: Testids.deliveryRegistries.tabVehicles
                        },
                        {
                            value: 'lcats',
                            label: registryTabLabel('lcats'),
                            testId: Testids.deliveryRegistries.tabLaborCategories
                        },
                        {
                            value: 'rate-book',
                            label: registryTabLabel('rate-book'),
                            testId: Testids.deliveryRegistries.tabRateBook
                        },
                        {
                            value: 'roles',
                            label: registryTabLabel('roles'),
                            testId: Testids.deliveryRegistries.tabRoleTypes
                        },
                        {
                            value: 'pricing-sheets',
                            label: registryTabLabel('pricing-sheets'),
                            testId: Testids.deliveryRegistries.tabPricingSheets
                        }
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "mt-6",
                    children: /*#__PURE__*/ _jsx(ActivePanel, {})
                })
            ]
        })
    });
}
